import type { PartnerSyncChecklist } from '../../../store/oneButtonStore';
import { financePartnerNormLower } from './utils/finance';

/** Checklist step keys that can be required per partner (excluding leadSyncAction and lead) */
export type PartnerSyncStepKey = 'leadSystem' | 'utility' | 'contact' | 'titleCheck' | 'creditCheck';

export interface PartnerSyncConfig {
    /** Which steps are required for this partner. Order matches display order. */
    requiredSteps: PartnerSyncStepKey[];
    /** Optional label overrides for steps (e.g. "Credit check (IGS and LightReach)") */
    stepLabels?: Partial<Record<PartnerSyncStepKey, string>>;
}

const DEFAULT_STEP_LABELS: Record<PartnerSyncStepKey, string> = {
    leadSystem: 'Lead System sync',
    utility: 'Utility sync',
    contact: 'Contact sync',
    titleCheck: 'Title check',
    creditCheck: 'Credit check',
};

/** Per-partner sync logic: which checklist steps apply and completion rules */
export const PARTNER_SYNC_CONFIG: Record<string, PartnerSyncConfig> = {
    LightReach: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck', 'creditCheck'],
        stepLabels: { creditCheck: 'Credit check (LightReach)' },
    },
    GoodLeap: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
    IGS: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck', 'creditCheck'],
        stepLabels: { creditCheck: 'Credit Check' },
    },
    Cash: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
    Enfin: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
};

export function getPartnerSyncConfig(partnerName: string): PartnerSyncConfig {
    return (
        PARTNER_SYNC_CONFIG[partnerName] ?? {
            requiredSteps: ['leadSystem', 'utility', 'contact', 'titleCheck', 'creditCheck'],
        }
    );
}

export function getStepLabel(partnerName: string, stepKey: PartnerSyncStepKey): string {
    const config = getPartnerSyncConfig(partnerName);
    return config.stepLabels?.[stepKey] ?? DEFAULT_STEP_LABELS[stepKey];
}

/** Returns true only if all steps required by this partner are done */
export function isPartnerSyncCompleteForConfig(
    partnerName: string,
    status: PartnerSyncChecklist | undefined
): boolean {
    if (!status) return false;
    const config = getPartnerSyncConfig(partnerName);
    for (const key of config.requiredSteps) {
        if (!status[key]) return false;
    }
    return true;
}

export const EMPTY_PARTNER_SYNC_CHECKLIST: PartnerSyncChecklist = {
    leadSyncAction: null,
    lead: false,
    leadSystem: false,
    utility: false,
    contact: false,
    titleCheck: false,
    creditCheck: false,
};

/**
 * Quote preflight: lead, contact, utility, and lead system only.
 * Title check and credit check remain visible in Partner Sync but do not block quote generation
 * (matches QuotesContractsCard / FinancingGridCard add-quote preflight).
 */
export function isPartnerSyncCompleteForQuote(status: PartnerSyncChecklist | undefined): boolean {
    if (!status) return false;
    return (
        status.lead === true &&
        status.contact === true &&
        status.utility === true &&
        status.leadSystem === true
    );
}

const normPartnerKey = (name: string | null | undefined) =>
    financePartnerNormLower(String(name ?? ''));

/**
 * Quote preflight: GoodLeap, Enfin, Cash, and Service Finance do not need the partner checklist
 * before adding quotes. IGS / LightReach (and other partners) still do.
 */
export function partnerRequiresQuoteSyncChecklist(partnerName: string | null | undefined): boolean {
    const n = normPartnerKey(partnerName);
    if (!n) return false;
    const compact = n.replace(/\s+/g, '');
    if (
        compact === 'cash' ||
        compact.includes('goodleap') ||
        compact.includes('enfin') ||
        compact.includes('servicefinance')
    ) {
        return false;
    }
    return true;
}

export function anyPartnerRequiresQuoteSyncChecklist(
    partners: Iterable<string | null | undefined>,
): boolean {
    for (const partner of partners) {
        if (partnerRequiresQuoteSyncChecklist(partner)) return true;
    }
    return false;
}

function syncApiFlagOn(value: unknown): boolean {
    return value === true || value === 1 || String(value).toLowerCase() === 'true';
}

export function syncStatusRowMatchesProductType(statusRow: any, productTypeName: string): boolean {
    const pt = String(statusRow?.ProductType ?? statusRow?.productType ?? '')
        .replace(/only/gi, '')
        .trim()
        .toLowerCase();
    const want = String(productTypeName || '')
        .replace(/\s+only$/i, '')
        .trim()
        .toLowerCase();
    if (!pt || !want) return false;
    return pt === want || pt.includes(want) || want.includes(pt);
}

/**
 * V1 `sync-sunnova-model` never calls `FinancePartnerTitleCheck` for LightReach, so the API's
 * title flag stays false for that partner no matter how many times the sync runs. Callers must
 * treat the step as satisfied instead of showing a checklist item that can never complete.
 */
export function partnerSkipsTitleCheck(partnerName: string | null | undefined): boolean {
    return normPartnerKey(partnerName).includes('lightreach');
}

/** V1 generate-quote `getSyncStatusList`: contact + lead + lead system + utility; IGS skips title. */
export function isV1ProductQuoteSyncComplete(
    statusRow: any,
    partnerName: string | null | undefined,
): boolean {
    if (!statusRow) return false;
    const n = normPartnerKey(partnerName);
    const isIgs = n === 'igs' || n.startsWith('igs');
    const titleNotRequired = isIgs || partnerSkipsTitleCheck(partnerName);
    return (
        syncApiFlagOn(statusRow.IsContactSync) &&
        syncApiFlagOn(statusRow.IsLeadCreate) &&
        syncApiFlagOn(statusRow.IsLeadSystem) &&
        syncApiFlagOn(statusRow.IsUtility) &&
        (titleNotRequired || syncApiFlagOn(statusRow.IsTilte) || syncApiFlagOn(statusRow.IsTitle))
    );
}

export function findSyncStatusRowForProduct(syncList: any[], productTypeName: string): any | undefined {
    if (!Array.isArray(syncList)) return undefined;
    return syncList.find((row) => syncStatusRowMatchesProductType(row, productTypeName));
}

function syncStatusRowLeadId(row: any): string {
    return String(
        row?.Finance_Partner_Sync_LeadID ??
            row?.Finance_Partner_Sync_LeadId ??
            row?.FinancePartnerSyncLeadId ??
            row?.finance_Partner_Sync_LeadID ??
            row?.finance_Partner_Lead_Sync_Id ??
            row?.LeadId ??
            row?.leadId ??
            row?.LeadID ??
            '',
    ).trim();
}

/** Store key for per-product checklist state (same partner on Solar + Battery must not share one object). */
export function financePartnerSyncStoreKey(
    partnerName: string,
    productTypeName?: string | null,
): string {
    const partner = String(partnerName || '').trim();
    const product = String(productTypeName || '')
        .trim()
        .replace(/\s+only$/i, '');
    if (!partner) return product || '';
    if (!product) return partner;
    return `${partner}::${product}`;
}

/** Prefer ProductType row (Solar vs Battery), then lead id — never another product's row on mixed deals. */
export function findSyncStatusRowForLeadOrProduct(
    syncList: any[],
    options: { leadId?: string | null; productTypeName?: string | null } = {},
): any | undefined {
    if (!Array.isArray(syncList) || syncList.length === 0) return undefined;
    const leadId = String(options.leadId || '').trim();
    const productTypeName = String(options.productTypeName || '').trim();

    if (productTypeName) {
        const forProduct = syncList.filter((row) => syncStatusRowMatchesProductType(row, productTypeName));
        if (forProduct.length === 1) return forProduct[0];
        if (forProduct.length > 1 && leadId) {
            const byBoth = forProduct.find((row) => syncStatusRowLeadId(row) === leadId);
            if (byBoth) return byBoth;
        }
        if (forProduct.length > 0) return forProduct[0];
    }

    if (leadId) {
        const byLead = syncList.find((row) => syncStatusRowLeadId(row) === leadId);
        if (byLead) return byLead;
    }

    return undefined;
}

export function checklistFromFinancePartnerSyncRow(row: any | undefined): Partial<PartnerSyncChecklist> {
    if (!row) return {};
    return {
        lead: row.IsLeadCreate === true,
        contact: row.IsContactSync === true,
        titleCheck: row.IsTilte === true || row.IsTitle === true,
        utility: row.IsUtility === true,
        leadSystem: row.IsLeadSystem === true,
        creditCheck: row.IsCreditPass === true,
    };
}

/**
 * Add-quote preflight. V1 does not block opening Generate Quote; when V2 still checks,
 * use the ProductType row (Solar / Battery), never `FinancePartnerSyncStatuses[0]` on a mixed deal.
 * No matching row → allow (V1 still opens the form).
 */
export function quoteSyncPreflightAllowsAddQuote(
    activeProducts: Array<{ productTypeName: string; partnerName: string; active?: boolean }>,
    syncList: any[],
): boolean {
    const rows = Array.isArray(syncList) ? syncList : [];
    for (const product of activeProducts) {
        if (product.active === false) continue;
        if (!partnerRequiresQuoteSyncChecklist(product.partnerName)) continue;
        const statusRow = findSyncStatusRowForProduct(rows, product.productTypeName);
        if (!statusRow) continue;
        if (!isV1ProductQuoteSyncComplete(statusRow, product.partnerName)) return false;
    }
    return true;
}

/**
 * LightReach eligible products API is available once utility, system design (lead system), and lead
 * are synced — same gates as Trinity before showing eligible products (credit/title not required).
 */
export function isLightreachEligibleProductsPrerequisiteMet(
    partnerName: string,
    status: PartnerSyncChecklist | undefined
): boolean {
    if (!normPartnerKey(partnerName).includes('lightreach')) return false;
    if (!status) return false;
    return status.utility === true && status.leadSystem === true && status.lead === true;
}
